// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { createReference, type MedplumClient } from '@medplum/core';
import type {
  Binary,
  Bundle,
  BundleEntry,
  Encounter,
  MedicationRequest,
  Patient,
  Practitioner,
  Provenance,
  Task,
} from '@medplum/fhirtypes';
import { DME_TASK_CODE_SYSTEM, DME_TASK_VENDOR_URL, SUBMIT_DME_ORDER_TASK_CODE } from '@polarissleep/shared';
import {
  buildPrescriptionDocumentEntries,
  buildPrescriptionRetirementEntries,
  type CurrentPrescriptionArtifacts,
  withoutOrderDocumentInputs,
  withPrescriptionDocumentInputs,
} from './prescription-pdf';
import { withTenantScope } from './tenant';

const CLOSED_TASK_STATUSES: ReadonlySet<NonNullable<Task['status']>> = new Set([
  'completed',
  'cancelled',
  'failed',
  'rejected',
  'entered-in-error',
]);

export const PRESCRIPTION_AMENDED_TASK_NOTE = 'Prescription amended by provider; the task now carries the amended PDF.';
/**
 * Amendments without a prescription PDF (referral orders): the superseded PDF inputs are removed from the Task, and
 * the referral form is attached once it is generated (right after signing, or when the encounter note is locked).
 */
export const PRESCRIPTION_AMENDED_WITHOUT_PDF_TASK_NOTE =
  'Prescription amended by provider; review the amended order for the updated details.';

export function isOpenTask(task: Task): boolean {
  return !CLOSED_TASK_STATUSES.has(task.status);
}

/** The most recently updated submit-dme-order Task for the order that has not been closed yet. */
export async function findOpenDmeTaskForOrder(
  medplum: MedplumClient,
  medicationRequestId: string
): Promise<Task | undefined> {
  const tasks = await medplum.searchResources(
    'Task',
    {
      focus: `MedicationRequest/${medicationRequestId}`,
      code: `${DME_TASK_CODE_SYSTEM}|${SUBMIT_DME_ORDER_TASK_CODE}`,
      _sort: '-_lastUpdated',
      _count: '20',
    },
    { cache: 'no-cache' }
  );
  return tasks.find(isOpenTask);
}

const OPEN_ENCOUNTER_STATUSES: ReadonlySet<Encounter['status']> = new Set([
  'planned',
  'arrived',
  'triaged',
  'in-progress',
  'onleave',
]);

/**
 * Whether a referral order's form should be generated right after signing. A referral on an encounter that is still
 * open gets its form when the encounter note is locked (`useEncounterNoteController`); every other referral —
 * including one amended after its encounter was finished — gets it immediately.
 */
export async function shouldGenerateReferralFormNow(
  medplum: MedplumClient,
  order: MedicationRequest
): Promise<boolean> {
  if (!order.encounter?.reference) {
    return true;
  }
  const encounter = await medplum
    .readReference<Encounter>(order.encounter, { cache: 'no-cache' })
    .catch((): undefined => undefined);
  return !encounter || !OPEN_ENCOUNTER_STATUSES.has(encounter.status);
}

export interface BuildPrescriptionOrderTransactionParams {
  patient: Patient;
  practitioner: Practitioner;
  /** The order to write. When it has an id it is an amendment and is written with PUT. */
  order: MedicationRequest;
  /** `urn:uuid:` fullUrl used to reference a new order from the other entries. Ignored when amending. */
  orderFullUrl: string;
  /** Task to create when there is no open Task to update. `focus` and prescription inputs are filled in here. */
  newTask: Task;
  signedAt: string;
  /** Uploaded prescription PDF. When present a DocumentReference (and DME ServiceRequest) is created. */
  binary?: Binary;
  /**
   * Amendment only: the currently active PDF documents / ServiceRequests that the amendment supersedes. Passed for
   * every amendment, including ones that produce no prescription PDF (e.g. a switch to a referral).
   */
  supersededArtifacts?: CurrentPrescriptionArtifacts;
  /** Amendment only: the original open submit-dme-order Task, updated in place instead of creating a new one. */
  openTask?: Task;
  /**
   * Amendment only: the version of the order the amendment was made from. The order PUT is conditional on it, so
   * amending a stale copy fails instead of reverting a newer amendment.
   */
  expectedOrderVersionId?: string;
}

/**
 * Builds one FHIR transaction for signing (or amending) a prescription: the MedicationRequest, the amendment
 * Provenance, the new PDF DocumentReference + ServiceRequest, retirement of the superseded PDF, and the DME Task
 * carrying the PDF input. Either everything is written or nothing is, so a failed amendment leaves the previous
 * PDF current and an order can never exist without its Task.
 */
export function buildPrescriptionOrderTransaction({
  patient,
  practitioner,
  order,
  orderFullUrl,
  newTask,
  signedAt,
  binary,
  supersededArtifacts,
  openTask,
  expectedOrderVersionId,
}: BuildPrescriptionOrderTransactionParams): Bundle {
  const amending = Boolean(order.id);
  const orderReference = order.id ? `MedicationRequest/${order.id}` : orderFullUrl;
  const entry: BundleEntry[] = [
    amending
      ? {
          resource: withTenantScope(order),
          request: {
            method: 'PUT',
            url: orderReference,
            ...(expectedOrderVersionId ? { ifMatch: `W/"${expectedOrderVersionId}"` } : {}),
          },
        }
      : {
          fullUrl: orderFullUrl,
          resource: withTenantScope(order),
          request: { method: 'POST', url: 'MedicationRequest' },
        },
  ];
  if (amending) {
    entry.push({
      resource: withTenantScope<Provenance>({
        resourceType: 'Provenance',
        target: [{ reference: orderReference }],
        recorded: signedAt,
        agent: [{ who: createReference(practitioner) }],
        activity: { coding: [{ system: 'http://polarissleep.com/order-actions', code: 'amend' }] },
      }),
      request: { method: 'POST', url: 'Provenance' },
    });
  }

  const document = binary
    ? buildPrescriptionDocumentEntries({
        patient,
        practitioner,
        request: order,
        medicationRequestReference: orderReference,
        binary,
        signedAt,
      })
    : undefined;
  if (document) {
    entry.push(...document.entries);
  }
  if (supersededArtifacts) {
    entry.push(...buildPrescriptionRetirementEntries(supersededArtifacts, 'amended'));
  }

  // An amended open Task drops its previous PDF inputs: they point at documents this amendment supersedes. A new
  // prescription PDF is attached below; a referral form is attached after the transaction, once it is generated.
  const baseTask: Task = openTask
    ? {
        ...withoutOrderDocumentInputs(openTask),
        priority: newTask.priority,
        restriction: newTask.restriction,
        description: newTask.description,
        extension: [
          ...(openTask.extension ?? []).filter((extension) => extension.url !== DME_TASK_VENDOR_URL),
          ...(newTask.extension ?? []).filter((extension) => extension.url === DME_TASK_VENDOR_URL),
        ],
        note: [
          ...(openTask.note ?? []),
          {
            text: document ? PRESCRIPTION_AMENDED_TASK_NOTE : PRESCRIPTION_AMENDED_WITHOUT_PDF_TASK_NOTE,
            time: signedAt,
            authorReference: createReference(practitioner),
          },
        ],
      }
    : { ...newTask, focus: { reference: orderReference } };
  const task = withTenantScope(
    document ? withPrescriptionDocumentInputs(baseTask, document.documentReference, document.attachment) : baseTask
  );
  entry.push(
    openTask?.id
      ? {
          resource: task,
          request: {
            method: 'PUT',
            url: `Task/${openTask.id}`,
            ...(openTask.meta?.versionId ? { ifMatch: `W/"${openTask.meta.versionId}"` } : {}),
          },
        }
      : { resource: task, request: { method: 'POST', url: 'Task' } }
  );

  return { resourceType: 'Bundle', type: 'transaction', entry };
}
